import { isRef } from '@vue/reactivity'
import isNil from 'lodash-es/isNil'
import { decode } from '@/utils/str'

const reformatUrl = (url: string, options: { [key: string]: any }) => {
  const uriParams: { [key: string]: string } = options.uriParams || {}
  const queryParams: { [key: string]: any } = options.query || {}

  if (uriParams) {
    if (url.indexOf('/') === 0) {
      url = url.substring(1)
    }
    // if (url.indexOf('api') !== 0) {
    //   url = 'api/' + url
    // }
    Object.keys(uriParams).forEach((key: string) => {
      const value = encodeURIComponent(uriParams[key])
      url = url.replace(key, value)
    })
  }

  if (Object.keys(queryParams).length > 0) {
    const queryString = new URLSearchParams(queryParams).toString()
    url += (url.includes('?') ? '&' : '?') + queryString
  }

  return url
}

const setKeyOptions = (options: { [key: string]: any } = {}, url: string) => {
  options.key = +new Date() + url
}

const setCacheOptions = (options: { [key: string]: any } = {}, value: boolean) => {
  options.initialCache = value
}

const errorProcess = (app: any, error: any) => {
  if (process.client) {
    let errorDataList = []
    if (error && error.value) {
      const errorData = error.value.data || {}
      // Http status code
      // const errorResponse = error.value.response || {}
      // const errorStatus = errorResponse.status || 500

      errorDataList = errorData.errors ? Array(errorData.errors) : errorData.errors
      if (typeof errorDataList === 'string') {
        $toast().error(errorDataList)
        return
      }

      if (Array.isArray(errorDataList) && errorDataList.length) {
        errorDataList.forEach((msg: any) => {
          if (Array.isArray(msg) && msg.length) {
            msg.forEach((msg2: any) => handleToastError(msg2))
            return
          }

          handleToastError(msg)
        })
      } else {
        $toast().error(app.$t('notifications.serverConnectionError'))
      }
    }
  }
}

const handleToastError = (msg: any) => {
  if (Array.isArray(msg)) {
    msg.forEach((msg2: any) => handleToastError(msg2))
    return
  }

  if (typeof msg === 'string') {
    $toast().error(decode(msg))
  }
  if (typeof msg === 'object' && 'msg' in msg) {
    $toast().error(String(msg.msg))
  }
  if (typeof msg === 'object' && 'message' in msg) {
    $toast().error(msg.message)
  }
}

const disableReactive = (data: any): any => {
  if (data instanceof FormData) {
    return data
  }
  if (Array.isArray(data)) {
    return data.map((d) => {
      return disableReactive(d)
    })
  } else if (typeof data === 'object') {
    data = { ...toRaw(data) }
    Object.keys(data).forEach((key) => {
      if (!(data[key] instanceof URLSearchParams || data[key] instanceof FormData)) {
        const raw = toRaw(data[key])
        if (Array.isArray(raw)) {
          data[key] = raw.map((o) => {
            return disableReactive(o)
          })
        } else if (raw !== null && typeof raw === 'object') {
          data[key] = { ...disableReactive(raw) }
        } else {
          data[key] = raw
        }
      }
    })
    return data
  } else {
    if (isReactive(data) || isRef(data)) {
      return toRaw(data)
    }
    return data
  }
}

export default async (source: string | { [key: string]: any }, options: { [key: string]: any } = {}, showError: Boolean = true): Promise<any> => {
  const app = useNuxtApp()
  const config = useRuntimeConfig().public
  const { auth } = $store()
  const router = useRouter()
  const workspaceId = auth.workspaceId
  const accessToken = auth.accessToken
  const requestHeaders: { [key: string]: any } = {
    Authorization: 'Bearer ' + accessToken,
    'HTS-WORKSPACE': workspaceId,
    'Accept-Language': 'vi'
  }
  if (isNil(options.initialCache)) {
    setCacheOptions(options, false)
  }
  options = disableReactive(options)

  try {
    if (typeof source === 'string') {
      setKeyOptions(options, reformatUrl(source, options))

      const response = await useFetch(reformatUrl(source, options), {
        baseURL: config.apiURL,
        headers: requestHeaders,
        ...options
      })
      if (response.status.value === 'error') {
        // Http status code
        const errorResponse: any = (response.error && response.error.value) || {}
        const errorStatus: any = (errorResponse && errorResponse.statusCode) || 500
        if (showError && errorStatus === 401) {
          auth.clear()
          router.push({
            name: 'auth-login'
          })
          return null
        }
        showError && errorProcess(app, response.error)
        Object.assign(response, {
          data: ref(response?.error?.value?.data)
        })
        return response
      }
      return response
    }

    const { method, url, headers } = source

    setKeyOptions(options, reformatUrl(url, options))

    const opts = {
      // eslint-disable-next-line space-before-function-paren
      onResponse({ response }: any) {
        response._data.headers = response.headers
        return response._data
      },
      baseURL: config.apiURL,
      headers: {
        ...headers,
        ...requestHeaders
      },
      ...options,
      ...{ method }
    }
    const response = await useFetch(reformatUrl(url, options), opts)
    // Http status code
    const errorResponse: any = (response.error && response.error.value) || {}
    const errorStatus: any = (errorResponse && errorResponse.statusCode) || 500
    if (showError && errorStatus === 401) {
      auth.clear()
      router.push({
        name: 'auth-login'
      })
      return null
    }
    if (response.status.value === 'error') {
      showError && errorProcess(app, response.error)
      Object.assign(response, {
        data: ref(response?.error?.value?.data)
      })
      return response
    }
    return response
  } catch (e: any) {
    $toast().error(e.message)
    throw e
  }
}
